import React from 'react';
import { Card, CardBody, Row, Col, Alert } from 'reactstrap';
import moment from 'moment';

const LeagueRegistrationConfirm = ({ registrationData }) => {
    const { schedule, teamName, player1, player2, notes } = registrationData;

    // Check if player 2 has any data
    const hasPlayer2 = player2.first_name || player2.last_name || player2.email || player2.phone_number;

    // Helper function to format time
    const formatTime = (timeString) => {
        if (!timeString) return 'N/A';
        const [hours, minutes] = timeString.split(':');
        const date = new Date();
        date.setHours(parseInt(hours, 10));
        date.setMinutes(parseInt(minutes, 10));
        return moment(date).format('h:mm A');
    };

    // Helper function to get day name
    const getDayName = (dayNum) => {
        const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        return days[dayNum] || 'N/A';
    };

    return (
        <div>
            <Card className="mb-3">
                <CardBody>
                    <Row className="mb-3">
                        <Col sm={4} className="fw-bold">Team Name:</Col>
                        <Col sm={8}>{teamName}</Col>
                    </Row>

                    <Row>
                        <Col sm={4} className="fw-bold">Schedule:</Col>
                        <Col sm={8}>{schedule.title}</Col>
                    </Row>
                    {schedule.day_of_week !== null && schedule.day_of_week !== undefined && (
                        <Row className="mt-2">
                            <Col sm={4} className="fw-bold">Day:</Col>
                            <Col sm={8}>{getDayName(schedule.day_of_week)}</Col>
                        </Row>
                    )}
                    {schedule.start_time && (
                        <Row className="mt-2">
                            <Col sm={4} className="fw-bold">Time:</Col>
                            <Col sm={8}>
                                {formatTime(schedule.start_time)}
                                {schedule.end_time && ` - ${formatTime(schedule.end_time)}`}
                            </Col>
                        </Row>
                    )}
                    {schedule.start_date && (
                        <Row className="mt-2">
                            <Col sm={4} className="fw-bold">Starts:</Col>
                            <Col sm={8}>{moment(schedule.start_date).format('MMMM D, YYYY')}</Col>
                        </Row>
                    )}
                    {schedule.duration_minutes && (
                        <Row className="mt-2">
                            <Col sm={4} className="fw-bold">Duration:</Col>
                            <Col sm={8}>{schedule.duration_minutes} minutes</Col>
                        </Row>
                    )}
                    {schedule.timeslots && schedule.timeslots.length > 0 && (
                        <Row className="mt-3">
                            <Col sm={4} className="fw-bold">Timeslots:</Col>
                            <Col sm={8}>
                                <div className="d-flex flex-wrap gap-2">
                                    {schedule.timeslots.map((timeslot, index) => (
                                        <span key={timeslot.id} className="badge bg-secondary">
                                            {timeslot.start} - {timeslot.end}
                                        </span>
                                    ))}
                                </div>
                            </Col>
                        </Row>
                    )}

                    <h6 className="mt-4 mb-3 text-secondary">Players</h6>
                    
                    {/* Player 1 */}
                    <Card className="mb-2 border">
                        <CardBody className="p-3">
                            <div className="d-flex align-items-center mb-2">
                                <span className="badge bg-secondary me-2">Player 1</span>
                                <h6 className="mb-0">{player1.first_name} {player1.last_name}</h6>
                            </div>
                            <Row className="g-2">
                                <Col xs={12} sm={6}>
                                    <div className="small">
                                        <strong className="text-muted">Email:</strong>
                                        <div className="text-break">{player1.email}</div>
                                    </div>
                                </Col>
                                <Col xs={12} sm={6}>
                                    <div className="small">
                                        <strong className="text-muted">Phone:</strong>
                                        <div>{player1.phone_number}</div>
                                    </div>
                                </Col>
                                {player1.tees && (
                                    <Col xs={12}>
                                        <div className="small">
                                            <strong className="text-muted">Tees:</strong> {player1.tees}
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </CardBody>
                    </Card>

                    {/* Player 2 */}
                    {hasPlayer2 && (
                        <Card className="mb-2 border">
                            <CardBody className="p-3">
                                <div className="d-flex align-items-center mb-2">
                                    <span className="badge bg-secondary me-2">Player 2</span>
                                    <h6 className="mb-0">
                                        {player2.first_name || player2.last_name 
                                            ? `${player2.first_name} ${player2.last_name}`.trim()
                                            : <span className="text-muted">Not provided</span>
                                        }
                                    </h6>
                                </div>
                                <Row className="g-2">
                                    <Col xs={12} sm={6}>
                                        <div className="small">
                                            <strong className="text-muted">Email:</strong>
                                            <div className="text-break">{player2.email || <span className="text-muted">—</span>}</div>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6}>
                                        <div className="small">
                                            <strong className="text-muted">Phone:</strong>
                                            <div>{player2.phone_number || <span className="text-muted">—</span>}</div>
                                        </div>
                                    </Col>
                                    {player2.tees && (
                                        <Col xs={12}>
                                            <div className="small">
                                                <strong className="text-muted">Tees:</strong> {player2.tees}
                                            </div>
                                        </Col>
                                    )}
                                </Row>
                            </CardBody>
                        </Card>
                    )}

                    {notes && (
                        <>
                            <h6 className="mt-4 mb-3 text-secondary">Additional Notes</h6>
                            <div className="bg-light p-3 rounded">
                                <p className="mb-0" style={{ whiteSpace: 'pre-wrap' }}>{notes}</p>
                            </div>
                        </>
                    )}
                </CardBody>
            </Card>

            <Alert color="warning" className="mb-0" fade={false}>
                <i className="fas fa-info-circle me-2"></i>
                <small><strong>Please review your information carefully.</strong> Once submitted, your team will be registered for this league.</small>
            </Alert>
        </div>
    );
};

export default LeagueRegistrationConfirm;
